/**
 * Navigate into a game URL in this tab.
 * Same-tab keeps one PeerJS identity for challenge join links (the advertised
 * challenger peerId stays the board). A second board tab used to require a
 * lobby proxy and often stalled ranked ready-start.
 *
 * @param {string} url
 * @param {{
 *   existingWindow?: Window | null,
 *   newTab?: boolean,
 *   allowSameTabFallback?: boolean
 * }} [options]
 * @returns {boolean} true when navigation or a tab open was started
 */
export function openGameTab(url, {
  existingWindow = null,
  newTab = false,
  allowSameTabFallback = true
} = {}) {
  if (!url || typeof window === 'undefined') {
    return false;
  }

  if (existingWindow && !existingWindow.closed) {
    try {
      existingWindow.location.href = url;
      try {
        existingWindow.opener = null;
      } catch {
        // Ignore opener clearing failures.
      }
      return true;
    } catch {
      // Fall through.
    }
  }

  if (!newTab) {
    window.location.assign(url);
    return true;
  }

  const opened = window.open(url, '_blank', 'noopener,noreferrer');
  if (opened) {
    try {
      opened.opener = null;
    } catch {
      // Ignore cross-origin opener clearing failures.
    }
    return true;
  }

  if (!allowSameTabFallback) {
    return false;
  }

  window.location.assign(url);
  return true;
}

/**
 * @deprecated Prefer same-tab openGameTab; kept for rare gesture-reserved tabs.
 */
export function openBlankGameTab() {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.open('about:blank', '_blank') || null;
}

export function hostGameUrl(gameId, publicUrlFn) {
  const base = typeof publicUrlFn === 'function' ? publicUrlFn() : '/';
  return `${base}?game=${encodeURIComponent(gameId)}&role=host`;
}
